"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

import type { StoreImage, StoreProduct } from "@/components/store/types";

type ProductArtworkProps = Readonly<{
  image?: StoreImage;
  name: string;
  sizes: string;
}>;

export const ProductArtwork = ({
  image,
  name,
  sizes,
}: ProductArtworkProps): React.ReactElement => {
  if (!image) {
    return (
      <div className="bg-primary/10 flex h-full w-full items-center justify-center">
        <Image
          src="/images/logos/logomark.png"
          alt=""
          width={84}
          height={84}
          className="opacity-80"
        />
      </div>
    );
  }

  return (
    <Image
      src={image.transformedUrl ?? image.url}
      alt={name}
      fill
      sizes={sizes}
      className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
    />
  );
};

type ProductCardProps = Readonly<{
  product: StoreProduct;
}>;

const CARD_SIZES = "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw";

const getSwatchColor = (product: StoreProduct, color: string): string => {
  return (
    product.variants.find((variant) => variant.colorName === color)
      ?.swatchColor ?? "#d4d4d8"
  );
};

export const ProductCard = ({
  product,
}: ProductCardProps): React.ReactElement => {
  const [selectedColor, setSelectedColor] = useState(product.colors[0] ?? "");

  const selectedImage = useMemo(() => {
    const colorImages = selectedColor
      ? product.imagesByColor[selectedColor]
      : undefined;

    return colorImages?.[0] ?? product.images[0];
  }, [product.images, product.imagesByColor, selectedColor]);

  const hasColorSelector = product.colors.length > 1;

  return (
    <article className="group border-border hover:border-primary/30 flex h-full min-w-0 flex-col overflow-hidden rounded-[1.5rem] border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(20,40,70,0.12)]">
      <Link
        href={`/products/${product.slug}`}
        className="bg-primary/5 relative aspect-square overflow-hidden"
      >
        <ProductArtwork
          image={selectedImage}
          name={product.name}
          sizes={CARD_SIZES}
        />

        {product.isSoldOut && (
          <span className="font-sora bg-foreground/80 absolute top-4 left-4 rounded-full px-3 py-1 text-xs font-semibold text-white backdrop-blur-xs">
            Sold out
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <Link
            href={`/products/${product.slug}`}
            className="font-sora group-hover:text-primary text-foreground line-clamp-2 text-lg font-semibold tracking-[-0.02em] transition-colors"
          >
            {product.name}
          </Link>
          {product.priceLabel && (
            <p className="font-sora text-primary text-base font-semibold sm:shrink-0">
              {product.priceLabel}
            </p>
          )}
        </div>

        <p className="text-secondary mt-3 line-clamp-2 flex-1 text-sm leading-6">
          {product.description}
        </p>

        {hasColorSelector && (
          <div
            className="mt-5 flex flex-wrap gap-2"
            role="group"
            aria-label="Available colors"
          >
            {product.colors.map((color) => {
              const isSelected = color === selectedColor;

              return (
                <button
                  key={color}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  aria-label={`Show ${color} ${product.name}`}
                  aria-pressed={isSelected}
                  title={color}
                  className={`flex h-11 w-11 cursor-pointer items-center justify-center rounded-full transition-all ${
                    isSelected ? "ring-primary/20 ring-2" : "hover:bg-primary/5"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className="h-7 w-7 rounded-full border border-black/10"
                    style={{ backgroundColor: getSwatchColor(product, color) }}
                  />
                </button>
              );
            })}
          </div>
        )}

        <div className="border-border mt-6 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <span className="border-border text-secondary bg-primary/5 rounded-full border px-3 py-1 text-xs font-semibold">
            {product.category || "Merch"}
          </span>

          <Link
            href={`/products/${product.slug}`}
            className="font-sora text-primary inline-flex items-center gap-1 text-xs font-semibold transition-all group-hover:translate-x-1"
          >
            {product.variants.length > 1 ? "Choose options" : "View product"}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
};

type ProductGridProps = Readonly<{
  products: readonly StoreProduct[];
}>;

export const ProductGrid = ({
  products,
}: ProductGridProps): React.ReactElement => {
  return (
    <div className="grid min-w-0 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {products.map((product) => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
};
